import {
	eventManager as DEBUGGING_EVENT_MANAGER,
	EVENT_TOPICS,
} from '@dealstack/frontend/shared/mechanism/debugging/eventManager';
import { useTranslationDefault } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks';
// eslint-disable-next-line no-restricted-imports
import { TFunction } from 'i18next';
import { toast, ToastContent, ToastOptions } from 'react-toastify';

import { DefaultToastLayout } from './DefaultToastLayout';
import { ILink } from './DefaultToastLayout.types';

DEBUGGING_EVENT_MANAGER.register(EVENT_TOPICS.NOTIFICATION_DISMISS_ALL, () => {
	toast.clearWaitingQueue();
	toast.dismiss();
});

const commonToastSettings: ToastOptions = {
	icon: false,
	closeOnClick: true,
};

interface Props {
	title?: string | React.ReactNode;
	message?: string | React.ReactNode;
	link?: ILink;
}

const callToast = (
	t: TFunction,
	type: 'info' | 'warning' | 'success' | 'error',
	{ title, message, link }: Props,
	toastConfig?: ToastOptions
) => {
	const preparedTitle = title ? title : t(`toast.default.title.${type}`);

	toast(
		({ closeToast }) => (
			<DefaultToastLayout
				type={type}
				link={link}
				onClose={closeToast}
				title={preparedTitle}
				message={message}
			/>
		),
		{
			...commonToastSettings,
			type,
			...toastConfig,
		}
	);
};

export const useDSToast = () => {
	const tDefault = useTranslationDefault();

	return {
		info: callToast.bind(null, tDefault, 'info'),
		/**
		 * @param toastProps Props for component
		 * @param toastConfig Config for react-toastify
		 */
		warning: (toastProps: Props, toastConfig?: ToastOptions) => {
			callToast(tDefault, 'warning', toastProps, toastConfig);
		},
		success: callToast.bind(null, tDefault, 'success'),
		/**
		 * @param toastProps Props for component
		 * @param toastConfig Config for react-toastify
		 */
		error: (toastProps: Props, toastConfig?: ToastOptions) => {
			callToast(tDefault, 'error', toastProps, toastConfig);
		},
		custom: (reactComponent: ToastContent, toastConfig?: ToastOptions) => {
			toast(reactComponent, {
				...commonToastSettings,
				type: 'default',
				...toastConfig,
			});
		},
	};
};
